import * as React from 'react'
import {FC} from 'react'
import {observer} from 'mobx-react'
import cn from 'classnames'
import {indexOf} from 'lodash'
import {PaymentMethod} from '@raisenow/epik'
import {useStores} from '../../../../lib/MobXProvider'
import {WidgetApi} from '../../../../lib/WidgetApi'
import {getPaymentMethodTitle} from '../../../../lib/helpers'
import {WidgetEventName} from '../../../../lib/WidgetEventer'
import {RippleWrapper} from '../../../wrappers/RippleWrapper'
import {RovingTabIndexWrapper} from '../../../wrappers/RovingTabIndexWrapper'
import {ErrorMessage} from '../../../ui/ErrorMessage'

///////////////////////////////////////////////////////////////////////////////

type PaymentPaymentMethodsProps = {
  topElementId?: string
  paymentMethods: PaymentMethod[]
}

///////////////////////////////////////////////////////////////////////////////

export const PaymentPaymentMethods: FC<PaymentPaymentMethodsProps> = observer(
  (props) => {
    const {api} = useStores<{api: WidgetApi}>()
    const {topElementId, paymentMethods} = props
    const form = api.paymentForm
    const errors = form.validationErrors
    const {uiTransitionTimeout: timeout} = api.computedConfig

    const setPaymentMethod = (value) => {
      setTimeout(async () => {
        form.setPaymentMethod(value)
        await api.events[WidgetEventName.PAYMENT_METHOD_CHANGED].publish({api})
        await api.ui.scrollTo('#' + topElementId)
        api.ui.focusWidgetElementBySelector(
          '.payment-method-details input[type=checkbox]',
        )
      }, timeout)
    }

    const errorId = api.ui.generateUniqueElementId('payment-method-error')
    const lastPaymentMethod = form.getLastSelectedPaymentMethod()
    let currentIdx = 0

    if (lastPaymentMethod) {
      const idx = indexOf(paymentMethods, lastPaymentMethod)

      if (idx !== -1) {
        currentIdx = idx
      }
    }

    return (
      <div className="payment-payment-methods">
        <ErrorMessage errors={errors} errorKey="payment_method" id={errorId} />

        <div className="main">
          <RovingTabIndexWrapper
            currentIdx={currentIdx}
            onSelect={form.setLastSelectedPaymentMethod.bind(form)}
          >
            <div
              className="payment-methods"
              role="radiogroup"
              aria-label={api.trans('blocks.payment_payment_methods.legend')}
            >
              {paymentMethods.map((paymentMethod, key) => {
                const paymentMethodTitle = getPaymentMethodTitle(
                  paymentMethod,
                  form,
                  api.trans.bind(api),
                )

                return (
                  <RippleWrapper key={key}>
                    <button
                      type="button"
                      className="payment-method"
                      id={api.ui.generateUniqueElementId(
                        'payment_method-' + paymentMethod,
                      )}
                      data-selectable={true}
                      data-value={paymentMethod}
                      role="radio"
                      aria-expanded={false}
                      aria-controls={api.ui.generateUniqueElementId(
                        'payment-method-form',
                      )}
                      aria-describedby={errorId}
                      onClick={() => setPaymentMethod(paymentMethod)}
                    >
                      <div
                        className={cn([
                          'image',
                          'payment-method-bg-image-round-' + paymentMethod,
                        ])}
                      />
                      <div className="text">{paymentMethodTitle}</div>
                    </button>
                  </RippleWrapper>
                )
              })}
            </div>
          </RovingTabIndexWrapper>
        </div>
      </div>
    )
  },
)
